import { Injectable } from '@angular/core';
import { cosmolexExport, cosmolexPrint } from '@core/util/accounting/cosmolex';
import { solunoExport, solunoPrint } from '@core/util/accounting/soluno';
import {
	ghostPracticeExport,
	ghostPracticePrint,
} from 'src/app/shared/core/util/accounting/ghost-practice';
import {
	AccountingExportIdentifierTypes,
	AccountingExportSettings,
	AccountingExportTypes,
} from '@models/businessSettings/settings';
import { AccountingExportingTypes, MatterBusinessUser } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Action, State } from '@ngxs/store';
import { StatementOfAccountTotals } from '@services/financialTotals/financial-totals.service';
import { NavigationService } from '@services/nav/nav.service';
import { BusinessState } from '@state/business';
import { UserProfileState } from '@state/user-profile';
import { CreateStatusType } from '@state/utils/models';
import { CosmolexAccountingItem } from 'src/app/private/sale/accounting/tables/cosmolex-accounting-items-table/cosmolex-accounting-items-table.component';
import { MatterDispatch } from '../matter.dispatch';
import { SelectedMatter } from '../matter.selectors';
import { MatterStatementofAccountSelectors } from '../statementOfAccounts/statementOfAccounts.selectors';
import * as actions from './accounting.actions';
import { MatterAccountingSelectors } from './accounting.selectors';
import { LeapCostRecoveryItem, LeapFeeTaskCodeItem } from './accounting.models';
import {
	LeapApiService,
	SoaExportResult,
} from '../../../api-repos/leap/leap-api.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { ICON } from '@core/util/icon';
import { Logger } from '@core/logger';
import { SolunoActivityCodeTypes } from '@models/soluno';
import {
	GhostPracticeItem,
	SolunoAccountingItem,
	SolunoItem,
} from 'src/app/private/sale/accounting/accounting.models';

export interface MatterAccountingModel {
	connected: boolean;
	loading: boolean;
	createStatus: CreateStatusType;
}

@State<MatterAccountingModel>({
	name: 'matterAccounting',
	defaults: {
		connected: false,
		loading: false,
		createStatus: null,
	},
})
@Injectable()
export class MatterAccountingState {
	@SelectSnapshot(UserProfileState.getCurrentBusinessId) businessId: string;

	@SelectSnapshot(SelectedMatter.matterNumber) fileNumber: string;
	@SelectSnapshot(SelectedMatter.matterName) fileName: string;
	@SelectSnapshot(SelectedMatter.matterAccountingNumber)
	accountingNumber: string;
	@SelectSnapshot(BusinessState.getAccountingExportSettings)
	accountingExportSettings: AccountingExportSettings;
	@SelectSnapshot(SelectedMatter.matterName) matterName: string;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterHST) hst: number;
	@SelectSnapshot(MatterStatementofAccountSelectors.statementOfAccountsTotals)
	totals: StatementOfAccountTotals;
	@SelectSnapshot(SelectedMatter.assignedLawyer)
	assignedLawyer: MatterBusinessUser;

	@SelectSnapshot(MatterAccountingSelectors.getCosmolexNonZeroHstItems)
	cosmolexHstItems: CosmolexAccountingItem[];
	@SelectSnapshot(MatterAccountingSelectors.getCosmolexNonZeroNonHstItems)
	cosmolexNonHstItems: CosmolexAccountingItem[];

	@SelectSnapshot(MatterAccountingSelectors.getSolunoNonZeroHstItems)
	solunoHstItems: SolunoItem[];
	@SelectSnapshot(MatterAccountingSelectors.getSolunoNonZeroNonHstItems)
	solunoNonHstItems: SolunoItem[];
	@SelectSnapshot(
		MatterAccountingSelectors.getSolunoNonZeroConvenyancingFeeItems,
	)
	solunoConvenyancingFeesItems: SolunoItem[];

	@SelectSnapshot(MatterAccountingSelectors.getLeapNonZeroHstItems)
	leapHstItems: LeapCostRecoveryItem[];
	@SelectSnapshot(MatterAccountingSelectors.getLeapNonZeroNonHstItems)
	leapNonHstItems: LeapCostRecoveryItem[];
	@SelectSnapshot(MatterAccountingSelectors.getLeapNonZeroConvenyancingFeeItems)
	leapConveyancingFeesItems: LeapFeeTaskCodeItem[];

	@SelectSnapshot(MatterAccountingSelectors.getGhostPracticeNonZeroHstItems)
	ghostPracticeHstItems: GhostPracticeItem[];
	@SelectSnapshot(MatterAccountingSelectors.getGhostPracticeNonZeroNonHstItems)
	ghostPracticeNonHstItems: GhostPracticeItem[];
	@SelectSnapshot(
		MatterAccountingSelectors.getGhostPracticeNonZeroConvenyancingFeeItems,
	)
	ghostPracticeConveyancingFeesItems: GhostPracticeItem[];

	constructor(
		private matterDispatch: MatterDispatch,
		private navService: NavigationService,
		private leapApiService: LeapApiService,
		private snackbarService: SnackbarService,
	) {}

	@Action(actions.PrintCosmolex)
	printCosmolex() {
		const url = cosmolexPrint(
			this.cosmolexHstItems,
			this.totals?.HSTDisbursementsHST,
			this.totals.HSTDisbursementsTotal,
			this.cosmolexNonHstItems,
			this.totals.nonHSTDisbursementsTotal,
			this.hst,
			this.accountingNumber,
			this.fileNumber,
			this.fileName,
		);

		this.navService.goToRemoteUrl(url, true);

		this.matterDispatch.SetAccountingExport(
			this.matterId,
			AccountingExportTypes.COSMOLEX,
			AccountingExportingTypes.PRINT,
		);

		return;
	}

	@Action(actions.PrintSoluno)
	printSoluno() {
		const url = solunoPrint(
			this.solunoHstItems
				.filter((x) => this.NotApplicableValueFilter(x.accountingItem))
				.map((x) => x.accountingItem),
			this.solunoNonHstItems
				.filter((x) => this.NotApplicableValueFilter(x.accountingItem))
				.map((x) => x.accountingItem),
			this.solunoConvenyancingFeesItems
				.filter((x) => this.NotApplicableValueFilter(x.accountingItem))
				.map((x) => x.accountingItem),
			this.totals,
			this.hst,
			this.accountingNumber,
			this.fileNumber,
			this.fileName,
		);

		this.navService.goToRemoteUrl(url, true);

		this.matterDispatch.SetAccountingExport(
			this.matterId,
			AccountingExportTypes.SOLUNO,
			AccountingExportingTypes.PRINT,
		);

		return;
	}

	@Action(actions.ExportSoluno)
	async exportSoluno() {
		const data = [
			...this.solunoHstItems.map((x) => x.accountingItem),
			...this.solunoNonHstItems.map((x) => x.accountingItem),
		]
			.filter(this.NotApplicableValueFilter)
			.map((x) => x);

		const accountingNumber =
			this.accountingExportSettings.accountingIdentifier ===
			AccountingExportIdentifierTypes.ACCOUNTING_NUMBER
				? this.accountingNumber
				: this.fileNumber;

		await solunoExport(
			this.solunoConvenyancingFeesItems
				.filter((x) => this.NotApplicableValueFilter(x.accountingItem))
				.map((x) => x.accountingItem),
			data,
			accountingNumber,
			this.matterName,
			this.assignedLawyer.name,
		);

		this.matterDispatch.SetAccountingExport(
			this.matterId,
			AccountingExportTypes.SOLUNO,
			AccountingExportingTypes.EXPORT,
		);
		return;
	}

	@Action(actions.ExportLeap)
	async exportLeap() {
		const costRecoveries = [
			...this.leapHstItems.map((x) => x.accountingItem),
			...this.leapNonHstItems.map((x) => x.accountingItem),
		];

		const fees = this.leapConveyancingFeesItems.map((x) => x.accountingItem);
		try {
			const response = await this.leapApiService
				.exportSoaToLeap({ matterId: this.matterId, costRecoveries, fees })
				.toPromise();

			switch (response) {
				case SoaExportResult.Failure:
					this.snackbarService.error(
						'Failed exporting activity and recovery codes to LEAP.',
					);
					break;
				case SoaExportResult.Success:
					this.snackbarService.openSnackBar({
						text: 'Successfully exported activity and recovery codes to LEAP.',
						type: 'success',
					});
					break;
				case SoaExportResult.PartialSuccess:
					this.snackbarService.openSnackBar(
						{
							text: 'Exported some but not all activity and recovery codes to LEAP.',
							type: 'success',
							icon: ICON.WARNING,
						},
						{
							duration: 7000,
						},
					);
					break;
				case SoaExportResult.Misconfiguration:
					this.snackbarService.openSnackBar(
						{
							text:
								'Cannot export activity and recovery codes to LEAP. You must configure them either ' +
								'at the business or matter level.',
							type: 'error',
							icon: ICON.WARNING,
						},
						{
							duration: 7000,
						},
					);
					break;
			}

			if (
				response === SoaExportResult.Success ||
				response === SoaExportResult.PartialSuccess
			) {
				this.matterDispatch.SetAccountingExport(
					this.matterId,
					AccountingExportTypes.LEAP,
					AccountingExportingTypes.EXPORT,
				);
			}
		} catch (error) {
			const message =
				'Unexpected error occurred during exporting activity and recovery codes.';

			Logger.error(
				message +
					' The unexpected error may be in our C# API or it may have occurred on the LEAP side. ' +
					JSON.stringify(error),
			);

			this.snackbarService.error(message);
		}
	}

	@Action(actions.ExportCosmolex)
	exportCosmolex() {
		const data = [...this.cosmolexHstItems, ...this.cosmolexNonHstItems];

		const accountingNumber =
			this.accountingExportSettings.accountingIdentifier ===
			AccountingExportIdentifierTypes.ACCOUNTING_NUMBER
				? this.accountingNumber
				: this.fileNumber;

		cosmolexExport(data, accountingNumber, this.fileNumber, this.fileName);

		this.matterDispatch.SetAccountingExport(
			this.matterId,
			AccountingExportTypes.COSMOLEX,
			AccountingExportingTypes.EXPORT,
		);
		return;
	}

	@Action(actions.ExportGhostPractice)
	exportGhostPractice() {
		const disbursements = [
			...this.ghostPracticeHstItems.map((x) => x.accountingItem),
			...this.ghostPracticeNonHstItems.map((x) => x.accountingItem),
		];

		const fees = this.ghostPracticeConveyancingFeesItems.map(
			(x) => x.accountingItem,
		);

		const accountingNumber =
			this.accountingExportSettings.accountingIdentifier ===
			AccountingExportIdentifierTypes.ACCOUNTING_NUMBER
				? this.accountingNumber
				: this.fileNumber;

		ghostPracticeExport(
			fees,
			disbursements,
			accountingNumber,
			this.fileNumber,
			this.fileName,
		);

		this.matterDispatch.SetAccountingExport(
			this.matterId,
			AccountingExportTypes.GHOSTPRACTICE,
			AccountingExportingTypes.EXPORT,
		);
		return;
	}

	@Action(actions.PrintGhostPractice)
	printGhostPractice() {
		const url = ghostPracticePrint(
			this.ghostPracticeHstItems.map((x) => x.accountingItem),
			this.ghostPracticeNonHstItems.map((x) => x.accountingItem),
			this.ghostPracticeConveyancingFeesItems.map((x) => x.accountingItem),
			this.totals,
			this.hst,
			this.accountingNumber,
			this.fileNumber,
			this.fileName,
		);

		this.navService.goToRemoteUrl(url, true);

		this.matterDispatch.SetAccountingExport(
			this.matterId,
			AccountingExportTypes.GHOSTPRACTICE,
			AccountingExportingTypes.PRINT,
		);

		return;
	}

	private NotApplicableValueFilter(item: SolunoAccountingItem): boolean {
		return item.activityCode?.type !== SolunoActivityCodeTypes.NA;
	}
}
